import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAgencyAuth } from "../../../contexts/AgencyAuthContext";
import { agencyService } from "../../../services/agency.service";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { ArenaLogo } from "@/components/common/ArenaLogo";
import { AxiosError } from "axios";

export function AgencyLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAgencyAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await agencyService.signIn({
        email: email,
        password: password,
      });

      login(response);
      navigate("/agency/dashboard");
    } catch (err) {
      if (err instanceof AxiosError) {
        // Handle Axios specific errors
        if (err.response?.status === 400) {
          setError("Invalid email or password");
        } else if (err.response?.status === 401) {
          setError("Unauthorized access. Please check your credentials.");
        } else if (err.response?.status === 403) {
          setError("Access forbidden. Please contact support.");
        } else if (err.response?.status === 404) {
          setError("Agency not found. Please check your email.");
        } else if (err.code === "ERR_NETWORK") {
          setError("Network error. Please check your internet connection.");
        } else {
          setError("An error occurred. Please try again later.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className=" md:m-auto mx-auto w-[98%] md:w-max pt-4 md:pt-0 md:h-max">
      <div className="  h-full mx-auto flex items-center justify-center  md:px-5 md:py-7 py-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]  ">
        <div className="w-full overflow-y-auto h-full flex items-center  justify-center md:px-5 h-full">
          <div className="h-full md:w-[380px]">
            <ArenaLogo className="w-12 border border-black mx-auto mb-4 rounded-[12px] bg-[#f6f6f6]" />
            <div className="mb-6 space-y-4">
              <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] leading-[32px] text-center align-middle">
                Sign in to Reveshare <br /> Partner Program
              </h1>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 pb-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-brand-600 hover:bg-brand-600/90"
                disabled={isLoading}
              >
                {isLoading ? "Logging in..." : "Login"}
              </Button>
            </form>

            <div className="mt-1 text-center h-[44px] flex items-center justify-center px-4 border border-brand-600 rounded-[8px]">
              <span className="font-bold text-[14px] leading-[20px] tracking-normal text-muted-foreground">
                Don't have an account?{" "}
              </span>
              <Link
                to="/agency/auth/signup"
                className="text-sm text-brand-600 font-bold text-[14px] tracking-normal hover:text-purple pl-1"
              >
                Sign up
              </Link>
            </div>
            <div className="mt-[20px] text-center">
              <Link
                to="/agency/auth/forgot-password"
                className="text-sm text-brand-600 font-bold text-[14px] tracking-normal hover:text-purple pl-1"
              >
                Forgot password
              </Link>
            </div>
            <div className="pt-[20px] w-[194px] m-auto font-normal text-[12px] leading-[16px] tracking-normal text-muted-foreground text-center">
              By signing in you agree to our <br />
              <Link
                to={"/terms"}
                className="text-brand-600 text-[12px] tracking-normal hover:text-purple pl-1"
              >
                terms of service
              </Link>{" "}
              and{" "}
              <Link
                to={"/policy"}
                className="text-brand-600  text-[12px] tracking-normal hover:text-purple pl-1"
              >
                privacy policy
              </Link>
              .
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
